// @greenlight-spec: 48-user-passthrough.md#test-sign-in
// @greenlight-spec: 28-it-dashboard-ui.md
import * as React from 'react';
import { CheckCircle2, ExternalLink, Loader2, XCircle } from 'lucide-react';

import { Button } from '@/components/ui/button';

import {
  SIGN_IN_TEST_CHANNEL,
  SIGN_IN_TEST_STALL_MS,
  hintsFor,
  parseSignInTestMessage,
  signInTestFailure,
  signInTestKey,
  signInTestUrl,
  withRedirectUri,
  type SignInTestMessage,
  type SignInTestTarget,
} from './signInTestModel';

export type SignInTestState =
  | { phase: 'waiting' }
  | { phase: 'stalled' }
  | { phase: 'done'; message: SignInTestMessage };

/**
 * Tracks every Test sign-in this view started. Each opens `/connect/start?test=1` in a
 * new tab; the result page there answers on a same-origin BroadcastChannel. A
 * test with no answer after a minute is shown as stalled but keeps listening,
 * because the person may still be signing in.
 */
export interface SignInTests {
  stateOf: (target: { integrationId: string; credentialSlug: string }) => SignInTestState | null;
  start: (target: { integrationId: string; credentialSlug: string }) => void;
}

/**
 * Hold this where it outlives a refresh of the integrations list, which
 * remounts the cards: the page, or the dialog step.
 */
export function useSignInTests(onSettled?: (message: SignInTestMessage) => void): SignInTests {
  const [states, setStates] = React.useState<Record<string, SignInTestState>>({});
  const timers = React.useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const started = React.useRef(new Set<string>());
  const settled = React.useRef(onSettled);
  settled.current = onSettled;

  React.useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') return undefined;
    const channel = new BroadcastChannel(SIGN_IN_TEST_CHANNEL);
    channel.onmessage = (event: MessageEvent) => {
      const message = parseSignInTestMessage(event.data);
      if (!message) return;
      const key = signInTestKey(message);
      if (!started.current.has(key)) return;
      clearTimeout(timers.current[key]);
      setStates((prev) => ({ ...prev, [key]: { phase: 'done', message } }));
      settled.current?.(message);
    };
    const pending = timers.current;
    return () => {
      channel.close();
      Object.values(pending).forEach(clearTimeout);
    };
  }, []);

  const start = React.useCallback((target: { integrationId: string; credentialSlug: string }) => {
    const key = signInTestKey(target);
    started.current.add(key);
    clearTimeout(timers.current[key]);
    setStates((prev) => ({ ...prev, [key]: { phase: 'waiting' } }));
    timers.current[key] = setTimeout(() => {
      setStates((prev) =>
        prev[key]?.phase === 'waiting' ? { ...prev, [key]: { phase: 'stalled' } } : prev,
      );
    }, SIGN_IN_TEST_STALL_MS);
  }, []);

  const stateOf = React.useCallback(
    (target: { integrationId: string; credentialSlug: string }) =>
      states[signInTestKey(target)] ?? null,
    [states],
  );

  return { stateOf, start };
}

/** Opens the test in a new tab; a link, so no pop-up blocker stands in the way. */
export function SignInTestButton({
  target,
  state,
  onStart,
}: {
  target: SignInTestTarget;
  state: SignInTestState | null;
  onStart: () => void;
}): React.JSX.Element {
  return (
    <Button asChild variant="outline" size="sm">
      <a
        href={signInTestUrl(target)}
        target="_blank"
        rel="noopener noreferrer"
        onClick={onStart}
        aria-label={`Test sign-in for credential ${target.credentialSlug}`}
        data-testid={`sign-in-test-${target.credentialSlug}`}
      >
        {state?.phase === 'done' ? 'Test again' : 'Test sign-in'}
        <ExternalLink className="ml-1.5 h-3.5 w-3.5" aria-hidden="true" />
      </a>
    </Button>
  );
}

/** What the test found, or what to check when the tab never came back. */
export function SignInTestResult({
  target,
  state,
  announcePass = false,
}: {
  target: SignInTestTarget;
  state: SignInTestState | null;
  /** A credential row's last-test badge already says a test passed; elsewhere, say it here. */
  announcePass?: boolean;
}): React.JSX.Element | null {
  if (!state) return null;
  const box = 'space-y-2 rounded-gl border border-border bg-muted/30 p-3 text-[12px]';
  if (state.phase === 'waiting') {
    return (
      <p
        className="flex items-center gap-2 text-[12px] text-muted-foreground"
        role="status"
        data-testid="sign-in-test-waiting"
      >
        <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
        Finish signing in on the new tab. This updates when it comes back.
      </p>
    );
  }
  if (state.phase === 'stalled') {
    const hints = hintsFor(target);
    return (
      <div className={box} role="status" data-testid="sign-in-test-stalled">
        <p className="font-medium text-foreground">The sign-in has not come back yet</p>
        <p className="text-muted-foreground">
          If the new tab is still open, finish there. If it shows an error from the provider
          instead, it is usually one of these:
        </p>
        <ul className="space-y-2">
          {hints.map((h) => (
            <li key={`${h.code ?? ''}${h.cause}`}>
              {h.code && <span className="font-mono text-foreground">{h.code}</span>}
              {h.code && ' · '}
              <span className="text-foreground">{h.cause}</span>
              <span className="block text-muted-foreground">{withRedirectUri(h.fix)}</span>
            </li>
          ))}
        </ul>
        <p className="text-muted-foreground">Fix the registration, then test again.</p>
      </div>
    );
  }
  const { message } = state;
  if (message.outcome === 'verified') {
    if (!announcePass) return null;
    return (
      <p
        className="flex items-center gap-2 text-[12px] text-foreground"
        role="status"
        data-testid="sign-in-test-verified"
      >
        <CheckCircle2
          className="h-3.5 w-3.5 shrink-0 text-[hsl(var(--status-green-text))]"
          aria-hidden="true"
        />
        Sign-in works
      </p>
    );
  }
  const failure = signInTestFailure(message, hintsFor(target));
  return (
    <div className={box} role="status" data-testid="sign-in-test-failed">
      <p className="flex items-center gap-2 font-medium text-foreground">
        <XCircle
          className="h-3.5 w-3.5 shrink-0 text-[hsl(var(--status-red-text))]"
          aria-hidden="true"
        />
        Test sign-in failed
        {failure.code && <span className="font-mono font-normal">· {failure.code}</span>}
      </p>
      <p className="text-foreground">{failure.cause}</p>
      {message.account && (
        <p className="text-muted-foreground">
          Signed in as <span className="font-mono">{message.account}</span>
        </p>
      )}
      <p className="text-foreground">
        <span className="font-medium">Fix: </span>
        {failure.fix}
      </p>
    </div>
  );
}

/**
 * The step after Register for per-user OAuth: one Test sign-in per registered
 * credential, so IT proves the setup before anyone else meets it.
 */
export type SignInTestPanelTarget = SignInTestTarget & { name: string };

export function SignInTestPanel({
  targets,
  onSettled,
}: {
  targets: readonly SignInTestPanelTarget[];
  onSettled?: (message: SignInTestMessage) => void;
}): React.JSX.Element {
  const { stateOf, start } = useSignInTests(onSettled);
  return (
    <div data-testid="sign-in-test-panel">
      <ul className="divide-y divide-border rounded-gl border border-border">
        {targets.map((t) => (
          <li key={signInTestKey(t)} className="space-y-2 p-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="text-[13px] font-medium text-foreground">{t.name}</span>
              <SignInTestButton target={t} state={stateOf(t)} onStart={() => start(t)} />
            </div>
            <SignInTestResult target={t} state={stateOf(t)} announcePass />
          </li>
        ))}
      </ul>
    </div>
  );
}
